import React, { useEffect, useState } from 'react';
import { FocusedLayout } from '../../components/layout/FocusedLayout';
import { userAPI, publicAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { formatDate, formatCurrency } from '../../utils/helpers';
import { formatApiErrorDetail } from '../../contexts/AuthContext';
import { Users2, Plus, ArrowLeft, Vote, Coins, Handshake, PiggyBank, Search, Shield, Lock, Globe, MessageSquare } from 'lucide-react';

const GROUP_TYPES = [
  ['savings_club', 'Savings Club'], ['investment_club', 'Investment Club'],
  ['family', 'Family Group'], ['church', 'Church Group'],
  ['burial_society', 'Burial Society'], ['cooperative', 'Cooperative'],
  ['business', 'Business Group'], ['social_club', 'Social Club'], ['custom', 'Custom'],
];
const FREQ_LABELS = { weekly: 'Weekly', fortnightly: 'Fortnightly', monthly: 'Monthly', quarterly: 'Quarterly', annually: 'Annually' };
const THRESHOLDS = [
  ['simple', 'Simple Majority (>50%)'],
  ['two_thirds', 'Two-Thirds (66.7%)'],
  ['three_quarters', 'Three-Quarters (75%)'],
  ['unanimous', 'Unanimous (100%)'],
];
const STATUS_STYLES = {
  active: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
  pending: 'bg-amber-500/15 text-amber-600 border-amber-500/30',
  suspended: 'bg-red-500/15 text-red-600 border-red-500/30',
  closed: 'bg-muted text-muted-foreground border-border',
  passed: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
  failed: 'bg-red-500/15 text-red-600 border-red-500/30',
  open: 'bg-blue-500/15 text-blue-600 border-blue-500/30',
  pending_admin: 'bg-amber-500/15 text-amber-600 border-amber-500/30',
  approved: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30',
  rejected: 'bg-red-500/15 text-red-600 border-red-500/30',
};

const EMPTY_GROUP = {
  name: '', description: '', type: 'savings_club', currency: '', privacy: 'public',
  settings: {
    min_members: 2, max_members: 20, contribution_amount: 100,
    contribution_frequency: 'monthly', contribution_type: 'fixed',
    voting_threshold: 'simple', auto_contributions: false,
    savings_enabled: true, growth_savings_enabled: false,
    allow_partial_withdrawals: true, allow_loans: false,
    allow_new_members_after_start: true, allow_member_exit: true,
    distribution_method: 'contribution_based',
  },
};

export const GroupsPage = () => {
  const [tab, setTab] = useState('my');
  const [myGroups, setMyGroups] = useState([]);
  const [publicGroups, setPublicGroups] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState({ group: null, members: [], proposals: [], contributions: [], audit: [] });
  const [createForm, setCreateForm] = useState(null);
  const [busy, setBusy] = useState(false);

  // proposal dialog
  const [showPropose, setShowPropose] = useState(false);
  const [propForm, setPropForm] = useState({ type: 'withdraw', title: '', description: '', amount: '', duration_hours: 72 });

  // contribute dialog
  const [showContribute, setShowContribute] = useState(false);
  const [contribAmount, setContribAmount] = useState('');

  // join private
  const [joinCodeMap, setJoinCodeMap] = useState({});

  const load = async () => {
    try {
      const [m, p, c] = await Promise.all([
        userAPI.listMyGroups(),
        userAPI.listPublicGroups(search ? { search } : {}),
        publicAPI.getCurrencies(true),
      ]);
      setMyGroups(m.data);
      setPublicGroups(p.data);
      setCurrencies(c.data);
    } catch (e) { toast.error('Failed to load'); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const openDetail = async (id) => {
    try {
      const [g, mem, prop, con, aud] = await Promise.all([
        userAPI.getGroup(id),
        userAPI.listMembers(id).catch(() => ({ data: [] })),
        userAPI.listProposals(id).catch(() => ({ data: [] })),
        userAPI.listContributions(id).catch(() => ({ data: [] })),
        userAPI.groupAudit(id).catch(() => ({ data: [] })),
      ]);
      setDetail({ group: g.data, members: mem.data, proposals: prop.data, contributions: con.data, audit: aud.data });
      setSelected(g.data);
    } catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed'); }
  };

  const submitCreate = async () => {
    if (!createForm.name || !createForm.currency) return toast.error('Name and currency required');
    setBusy(true);
    try {
      await userAPI.createGroup(createForm);
      toast.success('Group submitted for admin approval');
      setCreateForm(null);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed'); }
    finally { setBusy(false); }
  };

  const doJoin = async (g) => {
    try {
      await userAPI.joinGroup(g.id, joinCodeMap[g.id]);
      toast.success('Joined!');
      setJoinCodeMap({ ...joinCodeMap, [g.id]: '' });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed'); }
  };

  const doContribute = async () => {
    try {
      await userAPI.contribute(selected.id, parseFloat(contribAmount));
      toast.success('Contribution completed');
      setShowContribute(false); setContribAmount('');
      openDetail(selected.id);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed'); }
  };

  const doPropose = async () => {
    if (!propForm.title) return toast.error('Title required');
    const payload = propForm.type === 'withdraw' ? { amount: parseFloat(propForm.amount || 0), pool: 'operating' } :
                    propForm.type === 'distribution' ? { amount: parseFloat(propForm.amount || selected.treasury?.operating || 0) } :
                    propForm.type === 'change_contribution_amount' ? { amount: parseFloat(propForm.amount || 0) } :
                    {};
    try {
      await userAPI.createProposal(selected.id, {
        type: propForm.type, title: propForm.title, description: propForm.description,
        payload, duration_hours: propForm.duration_hours,
      });
      toast.success('Proposal opened for voting');
      setShowPropose(false);
      setPropForm({ type: 'withdraw', title: '', description: '', amount: '', duration_hours: 72 });
      openDetail(selected.id);
    } catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed'); }
  };

  const doVote = async (pid, vote) => {
    try {
      await userAPI.voteProposal(pid, vote);
      toast.success(`Voted ${vote}`);
      openDetail(selected.id);
    } catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed'); }
  };

  // ===== DETAIL VIEW =====
  if (selected && detail.group) {
    const g = detail.group;
    const treasury = g.treasury || {};
    const myMember = detail.members.find(m => m.user_id === detail.members[0]?.user_id) || null;
    return (
      <FocusedLayout title="Community Groups" description="Connect with the community">
        <div className="space-y-6" data-testid="group-detail-page">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <Button variant="ghost" size="sm" onClick={() => { setSelected(null); }} data-testid="back-groups">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <div className="flex gap-2">
              <Badge variant="outline" className={STATUS_STYLES[g.status]}>{g.status}</Badge>
              <Badge variant="outline">{g.type.replace('_', ' ')}</Badge>
              <Badge variant="outline">{g.privacy === 'public' ? <><Globe className="h-3 w-3 mr-1" /> Public</> : <><Lock className="h-3 w-3 mr-1" /> Private</>}</Badge>
            </div>
          </div>

          <div>
            <h1 className="text-3xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{g.name}</h1>
            {g.description && <p className="text-muted-foreground mt-1">{g.description}</p>}
            {g.join_code && (
              <div className="mt-2 inline-flex items-center gap-2 text-xs px-2 py-1 rounded bg-muted">
                Join code: <span className="font-mono font-medium">{g.join_code}</span>
              </div>
            )}
          </div>

          <div className="grid gap-3 md:grid-cols-4">
            <Card data-testid="treasury-operating">
              <CardContent className="pt-4">
                <p className="text-xs uppercase text-muted-foreground">Operating</p>
                <p className="text-2xl font-semibold mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{(treasury.operating || 0).toFixed(2)} <span className="text-sm text-muted-foreground">{g.currency}</span></p>
              </CardContent>
            </Card>
            <Card data-testid="treasury-savings">
              <CardContent className="pt-4">
                <p className="text-xs uppercase text-muted-foreground flex items-center gap-1"><PiggyBank className="h-3 w-3" /> Savings</p>
                <p className="text-2xl font-semibold mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{(treasury.savings || 0).toFixed(2)} <span className="text-sm text-muted-foreground">{g.currency}</span></p>
              </CardContent>
            </Card>
            <Card data-testid="treasury-emergency">
              <CardContent className="pt-4">
                <p className="text-xs uppercase text-muted-foreground flex items-center gap-1"><Shield className="h-3 w-3" /> Emergency</p>
                <p className="text-2xl font-semibold mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{(treasury.emergency || 0).toFixed(2)} <span className="text-sm text-muted-foreground">{g.currency}</span></p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <p className="text-xs uppercase text-muted-foreground">Members</p>
                <p className="text-2xl font-semibold mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{g.member_count}/{g.settings?.max_members || '∞'}</p>
                <p className="text-xs text-muted-foreground mt-1">{FREQ_LABELS[g.settings?.contribution_frequency] || 'monthly'} · {g.settings?.contribution_amount || 0} {g.currency}</p>
              </CardContent>
            </Card>
          </div>

          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => { setContribAmount(String(g.settings?.contribution_amount || '')); setShowContribute(true); }} data-testid="contribute-btn">
              <Coins className="h-4 w-4 mr-2" /> Contribute
            </Button>
            <Button variant="outline" onClick={() => setShowPropose(true)} data-testid="new-proposal-btn">
              <Vote className="h-4 w-4 mr-2" /> New Proposal
            </Button>
          </div>

          <Tabs defaultValue="proposals">
            <TabsList>
              <TabsTrigger value="proposals" data-testid="tab-proposals">Proposals ({detail.proposals.length})</TabsTrigger>
              <TabsTrigger value="members">Members ({detail.members.length})</TabsTrigger>
              <TabsTrigger value="contributions">Contributions ({detail.contributions.length})</TabsTrigger>
              <TabsTrigger value="audit">Audit ({detail.audit.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="proposals">
              <Card>
                <CardContent className="pt-6">
                  {detail.proposals.length === 0 ? (
                    <p className="text-center py-8 text-muted-foreground">No proposals yet.</p>
                  ) : (
                    <div className="space-y-3">
                      {detail.proposals.map(p => {
                        const total = p.votes_yes + p.votes_no + p.votes_abstain;
                        return (
                          <div key={p.id} className="p-4 rounded-lg border border-border" data-testid={`proposal-${p.id}`}>
                            <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                              <div>
                                <p className="font-medium">{p.title}</p>
                                <p className="text-xs text-muted-foreground mt-0.5">
                                  by {p.proposer_name} · {p.type.replace('_', ' ')} · {p.threshold} threshold
                                </p>
                              </div>
                              <Badge variant="outline" className={STATUS_STYLES[p.status] || ''}>{p.status.replace('_', ' ')}</Badge>
                            </div>
                            {p.description && <p className="text-sm text-muted-foreground mb-2">{p.description}</p>}
                            {p.payload?.amount != null && (
                              <p className="text-sm mb-2"><strong>Amount:</strong> {p.payload.amount} {g.currency}</p>
                            )}
                            <div className="flex items-center gap-4 text-sm">
                              <span className="text-emerald-600">Yes: {p.votes_yes}</span>
                              <span className="text-red-600">No: {p.votes_no}</span>
                              <span className="text-muted-foreground">Abstain: {p.votes_abstain}</span>
                              <span className="text-xs text-muted-foreground ml-auto">Ends {formatDate(p.end_at)}</span>
                            </div>
                            {p.status === 'open' && (
                              <div className="flex gap-2 mt-3">
                                <Button size="sm" onClick={() => doVote(p.id, 'yes')} data-testid={`vote-yes-${p.id}`}>Yes</Button>
                                <Button size="sm" variant="outline" onClick={() => doVote(p.id, 'no')} data-testid={`vote-no-${p.id}`}>No</Button>
                                <Button size="sm" variant="ghost" onClick={() => doVote(p.id, 'abstain')} data-testid={`vote-abstain-${p.id}`}>Abstain</Button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="members">
              <Card>
                <CardContent className="pt-6">
                  <div className="divide-y divide-border">
                    {detail.members.map(m => (
                      <div key={m.id} className="py-3 flex items-center justify-between" data-testid={`member-${m.id}`}>
                        <div>
                          <p className="font-medium">{m.user_name || m.user_email}</p>
                          <p className="text-xs text-muted-foreground">{m.user_email} · Joined {formatDate(m.joined_at)}</p>
                        </div>
                        <div className="text-right">
                          <Badge variant="outline">{m.role.replace('_', ' ')}</Badge>
                          <p className="text-xs text-muted-foreground mt-1">Contributed {m.contribution_total.toFixed(2)} {g.currency}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="contributions">
              <Card>
                <CardContent className="pt-6">
                  {detail.contributions.length === 0 ? (
                    <p className="text-center py-6 text-muted-foreground">No contributions yet.</p>
                  ) : (
                    <div className="divide-y divide-border">
                      {detail.contributions.map(c => (
                        <div key={c.id} className="py-3 flex items-center justify-between">
                          <div>
                            <p className="text-sm capitalize">{c.type} contribution</p>
                            <p className="text-xs text-muted-foreground">{formatDate(c.paid_at)}</p>
                          </div>
                          <p className="font-semibold text-emerald-600">+{c.amount.toFixed(2)} {c.currency}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="audit">
              <Card>
                <CardContent className="pt-6">
                  <div className="divide-y divide-border">
                    {detail.audit.map(a => (
                      <div key={a.id} className="py-2 flex items-center justify-between text-sm" data-testid={`audit-${a.id}`}>
                        <div>
                          <p className="font-medium capitalize">{a.action.replace(/_/g, ' ')}</p>
                          <p className="text-xs text-muted-foreground">{formatDate(a.created_at)} {a.reason ? `— ${a.reason}` : ''}</p>
                        </div>
                        {a.amount > 0 && <p className="font-mono text-xs">{a.amount.toFixed(2)}</p>}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Contribute dialog */}
          <Dialog open={showContribute} onOpenChange={setShowContribute}>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>Contribute to {g.name}</DialogTitle>
                <DialogDescription>Funds move from your main wallet to the group operating treasury.</DialogDescription>
              </DialogHeader>
              <div className="space-y-2">
                <Label>Amount ({g.currency})</Label>
                <Input type="number" step="0.01" value={contribAmount} onChange={(e) => setContribAmount(e.target.value)} data-testid="contribute-amount-input" />
                <p className="text-xs text-muted-foreground">Expected: {g.settings?.contribution_amount || 0} {g.currency} ({g.settings?.contribution_type || 'fixed'})</p>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowContribute(false)}>Cancel</Button>
                <Button onClick={doContribute} data-testid="confirm-contribute-btn">Contribute</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Propose dialog */}
          <Dialog open={showPropose} onOpenChange={setShowPropose}>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>New Proposal</DialogTitle>
                <DialogDescription>Members vote; if it passes and requires a withdrawal, it goes to the platform admin.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Type</Label>
                  <Select value={propForm.type} onValueChange={(v) => setPropForm({ ...propForm, type: v })}>
                    <SelectTrigger data-testid="prop-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="withdraw">Withdraw funds</SelectItem>
                      <SelectItem value="distribution">Distribute to members</SelectItem>
                      <SelectItem value="change_contribution_amount">Change contribution amount</SelectItem>
                      <SelectItem value="change_frequency">Change frequency</SelectItem>
                      <SelectItem value="pause_contributions">Pause auto-contributions</SelectItem>
                      <SelectItem value="close_group">Close group</SelectItem>
                      <SelectItem value="update_rules">Update rules</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Title</Label><Input value={propForm.title} onChange={(e) => setPropForm({ ...propForm, title: e.target.value })} data-testid="prop-title-input" /></div>
                <div><Label>Description</Label><Textarea rows={2} value={propForm.description} onChange={(e) => setPropForm({ ...propForm, description: e.target.value })} /></div>
                {['withdraw', 'distribution', 'change_contribution_amount'].includes(propForm.type) && (
                  <div><Label>Amount ({g.currency})</Label><Input type="number" step="0.01" value={propForm.amount} onChange={(e) => setPropForm({ ...propForm, amount: e.target.value })} data-testid="prop-amount-input" /></div>
                )}
                <div><Label>Voting window (hours)</Label><Input type="number" value={propForm.duration_hours} onChange={(e) => setPropForm({ ...propForm, duration_hours: parseInt(e.target.value) || 72 })} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowPropose(false)}>Cancel</Button>
                <Button onClick={doPropose} data-testid="submit-proposal-btn">Open Proposal</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </FocusedLayout>
    );
  }

  // ===== LIST VIEW =====
  return (
    <FocusedLayout title="Community Groups" description="Connect with the community">
      <div className="space-y-6" data-testid="groups-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-4xl font-semibold flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <Handshake className="h-8 w-8 text-purple-600" /> Community Savings
            </h1>
            <p className="mt-2 text-muted-foreground max-w-2xl">
              Stokvel, mukando, chama — pool money with people you trust, vote on how it&apos;s spent, and grow together.
            </p>
          </div>
          <Button onClick={() => setCreateForm({ ...EMPTY_GROUP, currency: currencies[0]?.symbol || '' })} data-testid="new-group-btn">
            <Plus className="h-4 w-4 mr-2" /> New Group
          </Button>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="my" data-testid="tab-my-groups">My Groups ({myGroups.length})</TabsTrigger>
            <TabsTrigger value="browse" data-testid="tab-browse-groups">Browse ({publicGroups.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="my">
            <Card>
              <CardContent className="pt-6">
                {myGroups.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">You haven&apos;t joined or created any groups yet.</p>
                ) : (
                  <div className="grid gap-3 md:grid-cols-2">
                    {myGroups.map(g => (
                      <button key={g.id} onClick={() => openDetail(g.id)}
                        className="text-left p-4 rounded-lg border border-border hover:bg-muted/60 transition-colors"
                        data-testid={`my-group-${g.id}`}>
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-medium">{g.name}</p>
                          <Badge variant="outline" className={STATUS_STYLES[g.status]}>{g.status}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mb-2">
                          {g.type.replace('_', ' ')} · {g.my_role?.replace('_', ' ')} · {g.member_count} member{g.member_count !== 1 ? 's' : ''}
                        </p>
                        <p className="text-sm text-emerald-600">Treasury: {((g.treasury?.operating || 0)).toFixed(2)} {g.currency}</p>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="browse">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center gap-2">
                  <Search className="h-4 w-4 text-muted-foreground" />
                  <Input placeholder="Search groups..." value={search} onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && load()} data-testid="search-groups-input" />
                </div>
              </CardHeader>
              <CardContent>
                {publicGroups.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground">No public groups yet. Be the first to create one.</p>
                ) : (
                  <div className="grid gap-3 md:grid-cols-2">
                    {publicGroups.map(g => (
                      <div key={g.id} className="p-4 rounded-lg border border-border" data-testid={`public-group-${g.id}`}>
                        <div className="flex items-center justify-between mb-1">
                          <p className="font-medium">{g.name}</p>
                          <Badge variant="outline">{g.type.replace('_', ' ')}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mb-2">{g.description || 'No description'}</p>
                        <p className="text-xs text-muted-foreground mb-3">
                          {g.member_count} member{g.member_count !== 1 ? 's' : ''} · {FREQ_LABELS[g.settings?.contribution_frequency]} contributions of {g.settings?.contribution_amount} {g.currency}
                        </p>
                        <Button size="sm" className="w-full" onClick={() => doJoin(g)} data-testid={`join-${g.id}`}>Join</Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create group dialog */}
      <Dialog open={!!createForm} onOpenChange={(v) => !v && setCreateForm(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Community Savings Group</DialogTitle>
            <DialogDescription>Your group will be submitted for platform admin approval before it goes live.</DialogDescription>
          </DialogHeader>
          {createForm && (
            <div className="space-y-3">
              <div className="grid gap-3 md:grid-cols-2">
                <div className="md:col-span-2"><Label>Group Name</Label><Input value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} data-testid="new-group-name-input" /></div>
                <div className="md:col-span-2"><Label>Description</Label><Textarea rows={2} value={createForm.description} onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })} /></div>
                <div>
                  <Label>Type</Label>
                  <Select value={createForm.type} onValueChange={(v) => setCreateForm({ ...createForm, type: v })}>
                    <SelectTrigger data-testid="new-group-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{GROUP_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Currency</Label>
                  <Select value={createForm.currency} onValueChange={(v) => setCreateForm({ ...createForm, currency: v })}>
                    <SelectTrigger data-testid="new-group-currency-select"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{currencies.map(c => <SelectItem key={c.symbol} value={c.symbol}>{c.symbol}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Privacy</Label>
                  <Select value={createForm.privacy} onValueChange={(v) => setCreateForm({ ...createForm, privacy: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="public">Public (searchable)</SelectItem><SelectItem value="private">Private (invite only)</SelectItem></SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Distribution</Label>
                  <Select value={createForm.settings.distribution_method} onValueChange={(v) => setCreateForm({ ...createForm, settings: { ...createForm.settings, distribution_method: v } })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="equal">Equal</SelectItem>
                      <SelectItem value="contribution_based">By contribution</SelectItem>
                      <SelectItem value="percentage">Percentage</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Contribution amount</Label><Input type="number" step="0.01" value={createForm.settings.contribution_amount} onChange={(e) => setCreateForm({ ...createForm, settings: { ...createForm.settings, contribution_amount: parseFloat(e.target.value) || 0 } })} data-testid="contrib-amount-input" /></div>
                <div>
                  <Label>Frequency</Label>
                  <Select value={createForm.settings.contribution_frequency} onValueChange={(v) => setCreateForm({ ...createForm, settings: { ...createForm.settings, contribution_frequency: v } })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(FREQ_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Min members</Label><Input type="number" value={createForm.settings.min_members} onChange={(e) => setCreateForm({ ...createForm, settings: { ...createForm.settings, min_members: parseInt(e.target.value) || 2 } })} /></div>
                <div><Label>Max members</Label><Input type="number" value={createForm.settings.max_members} onChange={(e) => setCreateForm({ ...createForm, settings: { ...createForm.settings, max_members: parseInt(e.target.value) || 50 } })} /></div>
                <div className="md:col-span-2">
                  <Label>Voting threshold</Label>
                  <Select value={createForm.settings.voting_threshold} onValueChange={(v) => setCreateForm({ ...createForm, settings: { ...createForm.settings, voting_threshold: v } })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{THRESHOLDS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid gap-2 md:grid-cols-2 pt-2">
                <div className="flex items-center gap-2"><Switch checked={createForm.settings.auto_contributions} onCheckedChange={(v) => setCreateForm({ ...createForm, settings: { ...createForm.settings, auto_contributions: v } })} /><Label>Auto contributions</Label></div>
                <div className="flex items-center gap-2"><Switch checked={createForm.settings.growth_savings_enabled} onCheckedChange={(v) => setCreateForm({ ...createForm, settings: { ...createForm.settings, growth_savings_enabled: v } })} /><Label>Smart Growth Savings</Label></div>
                <div className="flex items-center gap-2"><Switch checked={createForm.settings.allow_partial_withdrawals} onCheckedChange={(v) => setCreateForm({ ...createForm, settings: { ...createForm.settings, allow_partial_withdrawals: v } })} /><Label>Allow partial withdrawals</Label></div>
                <div className="flex items-center gap-2"><Switch checked={createForm.settings.allow_new_members_after_start} onCheckedChange={(v) => setCreateForm({ ...createForm, settings: { ...createForm.settings, allow_new_members_after_start: v } })} /><Label>Allow new members after start</Label></div>
                <div className="flex items-center gap-2"><Switch checked={createForm.settings.allow_member_exit} onCheckedChange={(v) => setCreateForm({ ...createForm, settings: { ...createForm.settings, allow_member_exit: v } })} /><Label>Allow member exit</Label></div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateForm(null)}>Cancel</Button>
            <Button onClick={submitCreate} disabled={busy} data-testid="submit-create-group">{busy ? 'Submitting...' : 'Submit for approval'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </FocusedLayout>
  );
};
